import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSeo } from "../lib/seo";
import { PROJECTS, SECTORS } from "../data/portfolio";
import { SERVICES } from "../data/site";
import { PageHero } from "../components/PageHero";
import { ProjectCard } from "../components/ProjectCard";
import { EASE, Lines, Reveal } from "../components/motion";
import { Cta, Eyebrow } from "../components/ui-bits";
import { ClientMarquee } from "../components/ClientMarquee";

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`border px-4 py-2 text-xs font-medium uppercase tracking-[0.14em] transition-colors duration-300 ${active ? "border-teal bg-teal text-mint" : "border-ink/20 text-ink/80 hover:border-ink"}`}
    data-testid={testid}
  >
    {children}
  </button>
);

const FilterRow = ({ label, items, value, onChange, prefix }) => (
  <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-6">
    <span className="eyebrow w-24 shrink-0">{label}</span>
    <div className="flex flex-wrap gap-2">
      <Chip active={!value} onClick={() => onChange("")} testid={`${prefix}-all`}>Tutti</Chip>
      {items.map((it) => (
        <Chip key={it.id} active={value === it.id} onClick={() => onChange(it.id)} testid={`${prefix}-${it.id}`}>{it.label}</Chip>
      ))}
    </div>
  </div>
);

export default function Progetti() {
  const [sector, setSector] = useState("");
  const [service, setService] = useState("");
  useSeo("Progetti di comunicazione e social media in Lombardia", "Il portfolio di B Digital Studio: social media, produzione foto e video, campagne Meta Ads e lanci di attività tra Como, Monza e Brianza, Varese e Lecco.");

  const list = useMemo(
    () => PROJECTS.filter((p) => (!sector || p.sector === sector) && (!service || p.services.includes(service))),
    [sector, service]
  );

  return (
    <>
      <PageHero
        testid="progetti-hero"
        eyebrow="Progetti"
        lines={["Ogni brand ha", "una storia."]}
        sublines={["Noi troviamo", "il modo giusto", "per raccontarla."]}
        intro="Non esiste una strategia che funzioni per tutti. Esistono persone, idee, obiettivi e identità diverse, che meritano di essere raccontate in modo autentico."
      />
      <section className="px-5 pb-20 sm:px-10 md:pb-28 lg:px-16" data-testid="progetti-intro">
        <div className="grid grid-cols-12 gap-y-8 lg:gap-x-10">
          <Reveal className="col-span-12 md:col-span-6">
            <p className="text-base leading-relaxed text-ink/80 md:text-lg">
              In B Digital Studio lavoriamo a fianco di realtà molto diverse tra loro, costruendo progetti di comunicazione che uniscono strategia, creatività e contenuti capaci di lasciare il segno.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="col-span-12 md:col-span-5 md:col-start-8">
            <p className="text-base leading-relaxed text-ink/80 md:text-lg">
              Dalla costruzione di un'identità digitale al lancio di un nuovo brand, dalla produzione di contenuti alla gestione di campagne pubblicitarie: ogni progetto è una nuova sfida e un'opportunità per trasformare le idee in risultati.
            </p>
          </Reveal>
        </div>
        <Reveal className="mt-20"><p className="font-serif text-2xl md:text-3xl">Questi sono alcuni dei brand che hanno scelto di raccontarsi insieme a noi.</p></Reveal>
      </section>
      <ClientMarquee />
      <section className="px-5 pb-24 pt-24 sm:px-10 md:pb-36 md:pt-32 lg:px-16" data-testid="portfolio">
        <div className="mb-12 grid grid-cols-12 items-end gap-y-6 lg:gap-x-10">
          <div className="col-span-12 lg:col-span-3"><Reveal><Eyebrow n="01">Portfolio</Eyebrow></Reveal></div>
          <div className="col-span-12 lg:col-span-9"><Lines lines={["I nostri progetti"]} className="text-4xl leading-[1.1] sm:text-5xl lg:text-6xl" /></div>
        </div>
        <div className="flex flex-col gap-5 border-y border-ink/10 py-8">
          <FilterRow label="Settore" prefix="filter-sector" value={sector} onChange={setSector} items={SECTORS.map((s) => ({ id: s, label: s }))} />
          <FilterRow label="Servizio" prefix="filter-service" value={service} onChange={setService} items={SERVICES.map((s) => ({ id: s.id, label: s.title }))} />
        </div>
        <p className="mt-6 text-sm text-ink/70" data-testid="portfolio-count" aria-live="polite">{list.length} {list.length === 1 ? "progetto" : "progetti"}</p>
        <motion.div layout className="mt-12 grid grid-cols-1 gap-x-10 gap-y-16 md:grid-cols-2">
          <AnimatePresence mode="popLayout">
            {list.map((p, i) => (
              <motion.div
                layout
                key={p.slug}
                className={i % 2 === 1 ? "md:mt-32" : ""}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.7, ease: EASE }}
              >
                <ProjectCard p={p} index={PROJECTS.indexOf(p)} aspect="aspect-[4/5]" />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        {list.length === 0 && (
          <p className="mt-10 font-serif text-2xl" data-testid="portfolio-empty">Nessun progetto per questa combinazione. Prova un altro filtro.</p>
        )}
        <Reveal className="mt-24 border-t border-ink/15 pt-12 md:mt-32" data-testid="portfolio-more-clients">
          <p className="max-w-3xl font-serif text-3xl leading-snug md:text-5xl">
            <span className="text-teal">+30</span> altri clienti si sono affidati a noi negli ultimi anni.
          </p>
        </Reveal>
        <p className="mt-16 text-xs text-ink/60">Riferimenti visivi indicativi, non immagini originali dei clienti.</p>
      </section>
      <section className="bg-mint px-5 py-24 text-teal sm:px-10 md:py-36 lg:px-16" data-testid="progetti-next">
        <div className="grid grid-cols-12 gap-y-12 lg:gap-x-10">
          <div className="col-span-12 lg:col-span-8">
            <Lines lines={["Il prossimo progetto", "potrebbe essere il tuo."]} className="text-[2.05rem] leading-[1.06] sm:text-6xl lg:text-7xl" />
          </div>
          <Reveal delay={0.2} className="col-span-12 flex flex-col justify-end gap-8 lg:col-span-4">
            <p className="text-base leading-relaxed text-teal/90">
              Ogni collaborazione parte da una conversazione, da un obiettivo da raggiungere o da un'idea che ha bisogno della strategia giusta per prendere forma.
            </p>
            <p className="font-serif text-2xl">Hai un brand da raccontare? Facciamolo insieme.</p>
            <div><Cta to="/contatti" variant="teal" testid="progetti-cta">Parliamo del tuo progetto</Cta></div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
